import { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, Search } from 'lucide-react';

export default function GlassTable({
  columns, data, loading, emptyMessage = 'No data found', emptyIcon,
  onRowClick, actions, searchValue, onSearchChange, searchPlaceholder,
  testId = 'glass-table',
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    if (!sortKey || !data) return data || [];
    return [...data].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  return (
    <div className="glass-card relative overflow-hidden" data-testid={testId}>
      {/* 3D glass sheen */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-transparent pointer-events-none z-[1] rounded-[inherit]" />

      {onSearchChange != null && (
        <div className="relative z-[2] px-5 pt-5 pb-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-3 bg-white/[0.06] border border-white/10 rounded-xl px-4 h-11">
            <Search size={16} className="text-white/40 flex-shrink-0" />
            <input
              data-testid={`${testId}-search`}
              className="flex-1 bg-transparent text-white text-sm placeholder:text-white/30 outline-none"
              placeholder={searchPlaceholder || 'Search...'}
              value={searchValue || ''}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="relative z-[2] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white/[0.06] backdrop-blur-sm border-b border-white/10">
              {columns.map(col => (
                <th
                  key={col.key}
                  data-testid={`${testId}-th-${col.key}`}
                  className={`px-5 py-3.5 text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap ${col.sortable !== false ? 'cursor-pointer hover:text-white/70 select-none' : ''}`}
                  style={{ textAlign: col.align || 'left', width: col.width }}
                  onClick={col.sortable !== false ? () => toggleSort(col.key) : undefined}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {col.label}
                    {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </span>
                </th>
              ))}
              {actions && <th className="px-5 py-3.5 text-right text-xs font-semibold text-white/50 uppercase tracking-wider w-[120px]">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skel-${i}`} className="border-b border-white/[0.04]">
                  {columns.map(col => (
                    <td key={col.key} className="px-5 py-4"><div className="h-4 w-20 bg-white/[0.06] rounded animate-pulse" /></td>
                  ))}
                  {actions && <td className="px-5 py-4 text-right"><div className="h-4 w-16 bg-white/[0.06] rounded animate-pulse ml-auto" /></td>}
                </tr>
              ))
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="py-20 text-center">
                  {emptyIcon && <div className="flex justify-center mb-4">{emptyIcon}</div>}
                  <p className="text-white/40 text-sm" data-testid={`${testId}-empty`}>{emptyMessage}</p>
                </td>
              </tr>
            ) : sorted.map((row, i) => (
              <tr
                key={row.id || i}
                data-testid={`${testId}-row-${row.id || i}`}
                className={`border-b border-white/[0.04] transition-colors duration-150 hover:bg-white/[0.06] ${i % 2 === 0 ? 'bg-white/[0.02]' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map(col => (
                  <td key={col.key} className="px-5 py-4 text-white/80 whitespace-nowrap" style={{ textAlign: col.align || 'left' }}>
                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                  </td>
                ))}
                {actions && (
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">{actions(row)}</div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
